import { z } from 'zod';
import { apiGet, demoMode } from './api';
import { DEMO_TENANT } from './demo';
import { defaultSalonSettings, setProvisionedSalonSettings } from './salonSettings';

// Схема tenant — то, что реально нужно клиенту и владельцу.
// Валидация на границе: если бэкенд отдал мусор, падаем рано и явно.
export const TenantSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  city: z.string().nullable(),
  timezone: z.string(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  theme: z
    .object({
      accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
      background: z.string().optional(),
      text: z.string().optional(),
      mood: z.string().optional()
    })
    .default({}),
  business: z
    .object({
      tagline: z.string().optional(),
      about: z.string().optional(),
      highlights: z
        .array(z.object({ title: z.string(), text: z.string() }))
        .max(3)
        .optional(),
      addressHint: z.string().optional(),
      mapUrl: z.string().optional(),
      social: z.record(z.string(), z.string().nullable()).optional()
    })
    .default({}),
  demo: z.boolean()
});

export type Tenant = z.infer<typeof TenantSchema>;

// Slug салона для этого деплоя. Один сайт — один салон, без /s/<slug>/ в URL.
// Переопределяется переменной сборки VITE_TENANT, по умолчанию 'demo'.
export const DEFAULT_TENANT_SLUG = import.meta.env.VITE_TENANT || 'demo';

// Витрина салона. Публичный эндпоинт, ничего секретного не отдаёт.
export async function fetchTenant(_slug: string): Promise<Tenant> {
  if (demoMode) {
    try {
      const response = await fetch(`/tenants/${encodeURIComponent(_slug)}/business.json`, {
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const business = (await response.json()) as Record<string, unknown>;
      const base = defaultSalonSettings();
      const settings = {
        ...base,
        name: business.name ?? base.name,
        tagline: business.tagline ?? base.tagline,
        phone: business.phone ?? base.phone,
        address: business.address ?? base.address,
        addressHint: business.addressHint ?? base.addressHint,
        mapUrl: business.mapUrl ?? base.mapUrl,
        about: business.about ?? base.about,
        schedule: business.schedule ?? base.schedule,
        availableTimes: business.availableTimes ?? base.availableTimes,
        services: business.services ?? base.services,
        staff: business.staff ?? base.staff,
        gallery: business.gallery ?? base.gallery
      };
      setProvisionedSalonSettings(settings);
      return TenantSchema.parse({
        id: `demo-${_slug}`,
        slug: _slug,
        name: business.name,
        city: business.city ?? null,
        timezone: business.timezone ?? 'Asia/Krasnoyarsk',
        phone: business.phone ?? null,
        address: business.address ?? null,
        theme: business.theme ?? {},
        business,
        demo: true
      });
    } catch {
      return TenantSchema.parse({ ...DEMO_TENANT, demo: true });
    }
  }

  const data = await apiGet<unknown>('/tenant');
  return TenantSchema.parse({ ...(data as Record<string, unknown>), demo: false });
}
